import { useEffect, useMemo } from 'react';
import { usePmsIntegrationEnabled } from '@/features/payments/hooks/usePmsIntegrationEnabled';
import { useLazyGetMaintenanceListQuery, IMaintenanceListResponse } from '@/store/api/maintenanceApi';
import { splitMaintenanceTickets } from '@/utils/residents/maintenance/splitTickets';

// Align with iOS: sort primarily by updatedOn desc when both present.
// If one is missing updatedOn, items WITH updatedOn come first (newer);
// if both are missing, preserve existing order (no createdOn fallback).
const byIosUpdatedDesc = (a: IMaintenanceListResponse, b: IMaintenanceListResponse) => {
  const aU = a.updatedOn ? new Date(a.updatedOn).getTime() : undefined;
  const bU = b.updatedOn ? new Date(b.updatedOn).getTime() : undefined;
  if (aU != null && bU != null) return bU - aU;
  if (aU != null && bU == null) return -1;
  if (aU == null && bU != null) return 1;
  return 0;
};

export const useMaintenanceTickets = () => {
  const pmsEnabled = usePmsIntegrationEnabled();
  const [getMaintenanceList, { data, error, isLoading, isFetching, isUninitialized }] = useLazyGetMaintenanceListQuery();

  useEffect(() => {
    if (pmsEnabled) {
      getMaintenanceList();
    }
  }, [getMaintenanceList, pmsEnabled]);

  const allTickets = useMemo<IMaintenanceListResponse[] | undefined>(() => {
    const tickets = data?.response;
    if (!tickets) return undefined;
    return [...tickets].sort(byIosUpdatedDesc);
  }, [data]);

  const { openTickets, closedTickets } = useMemo(() => {
    if (!allTickets) return { openTickets: undefined, closedTickets: undefined };
    const { open, closed } = splitMaintenanceTickets(allTickets);
    return { openTickets: open, closedTickets: closed };
  }, [allTickets]);

  const loading = isLoading || isFetching || isUninitialized;
  const refresh = () => getMaintenanceList();

  return { openTickets, closedTickets, allTickets, loading, error, refresh };
};


